import { useEffect, useState } from "react";
import { IconTrash } from "@tabler/icons-react";
import { useCecos, useCuentas, useIndicadoresImpuesto } from "@/features/catalogos/hooks/use-catalogos";
import { useEditarSubLinea, useEliminarLinea } from "@/features/prorrateo/hooks/use-prorrateo-mutations";
import type { LineaProrrateo } from "@/features/prorrateo/types";
import { Badge, IconAction, NumberInput, Select, Td, Text, Tr } from "@/components/ui";

const GUID_VACIO = "00000000-0000-0000-0000-000000000000";

interface Props {
  linea: LineaProrrateo;
  disabled: boolean;
  solicitudId: string;
  /** true si el concepto tiene más de una sub-línea (se puede eliminar esta). */
  puedeEliminar: boolean;
  /** Slot del CFDI (Múltiples CFDI) · omitir para CFDI único. */
  numeroCfdi?: number;
}

/**
 * Fila de una SUB-LÍNEA de concepto · editable inline (autoguardado). El usuario puede
 * cambiar Centro de costo, Cuenta contable (default del concepto), Importe e Indicador.
 * La clave/descripción/importe del concepto viven en el encabezado del concepto (no aquí).
 * Se puede eliminar mientras el concepto conserve al menos una sub-línea.
 */
export function SubLineaRow({ linea, disabled, solicitudId, puedeEliminar, numeroCfdi }: Props) {
  const { data: cecos } = useCecos();
  const { data: cuentas } = useCuentas();
  const { data: indicadores } = useIndicadoresImpuesto();
  const editar = useEditarSubLinea(solicitudId, numeroCfdi);
  const eliminar = useEliminarLinea(solicitudId, numeroCfdi);

  const [importe, setImporte] = useState(linea.importe);
  useEffect(() => setImporte(linea.importe), [linea.importe]);

  const sinCuenta = !linea.cuentaContableCodigo;

  const guardar = (
    patch: Partial<{ centroDeCostoId: string; cuentaContableId: string; importe: number; indicador: string }>
  ) =>
    editar.mutate({
      lineaId: linea.id,
      update: {
        centroDeCostoId: patch.centroDeCostoId ?? linea.centroDeCostoId,
        cuentaContableId: patch.cuentaContableId ?? linea.cuentaContableId,
        importe: patch.importe ?? linea.importe,
        indicador: patch.indicador ?? linea.indicador,
      },
    });

  const indicadorOptions = indicadores.some((i) => i.codigo === linea.indicador)
    ? indicadores
    : [...indicadores, { id: linea.indicador, codigo: linea.indicador, tasaOCuota: 0, descripcion: "(dado de baja)", activo: false }];

  return (
    <Tr>
      <Td className="pl-8 font-mono text-muted-foreground">{linea.numeroLinea}</Td>
      {/* CeCo */}
      <Td>
        {disabled ? (
          <Text size="sm">{linea.centroDeCostoCodigo || "—"}</Text>
        ) : (
          <Select
            size="xs"
            options={cecos.map((c) => ({ value: c.id, label: `${c.codigo} · ${c.nombre}` }))}
            value={linea.centroDeCostoId === GUID_VACIO ? undefined : linea.centroDeCostoId}
            onValueChange={(v) => guardar({ centroDeCostoId: v })}
            placeholder="Buscar CeCo…"
            searchable
            searchPlaceholder="Código o nombre…"
            className="w-44"
          />
        )}
      </Td>
      {/* Cuenta · ahora EDITABLE (default del concepto) */}
      <Td>
        {disabled ? (
          <Text size="sm">{linea.cuentaContableCodigo || "—"}</Text>
        ) : sinCuenta && cuentas.length === 0 ? (
          <Text size="xs" fw={600} c="peligro">
            Sin cuenta
          </Text>
        ) : (
          <Select
            size="xs"
            options={cuentas.map((c) => ({ value: c.id, label: `${c.codigo} · ${c.nombre}` }))}
            value={linea.cuentaContableId === GUID_VACIO ? undefined : linea.cuentaContableId}
            onValueChange={(v) => guardar({ cuentaContableId: v })}
            placeholder="Cuenta…"
            searchable
            searchPlaceholder="Código o nombre…"
            className="w-40"
          />
        )}
      </Td>
      {/* Importe */}
      <Td align="right">
        {disabled ? (
          <Text size="sm" fw={500}>
            ${linea.importe.toFixed(2)}
          </Text>
        ) : (
          <NumberInput
            size="xs"
            value={importe}
            onChange={(v) => setImporte(typeof v === "number" ? v : 0)}
            onBlur={() => {
              if (importe !== linea.importe) guardar({ importe });
            }}
            min={0}
            step={0.01}
            decimals={2}
            hideControls
            className="w-27 text-right"
          />
        )}
      </Td>
      {/* Indicador */}
      <Td>
        {disabled ? (
          <Badge variant="outline" color="multiva" uppercase={false}>
            {linea.indicador}
          </Badge>
        ) : (
          <Select
            size="xs"
            options={indicadorOptions.map((i) => ({ value: i.codigo, label: `${i.codigo} · ${(i.tasaOCuota * 100).toFixed(0)}%` }))}
            value={linea.indicador}
            onValueChange={(v) => guardar({ indicador: v })}
            className="w-27"
          />
        )}
      </Td>
      {/* Eliminar sub-línea (solo si el concepto conserva ≥1) */}
      <Td align="right">
        {!disabled && puedeEliminar && (
          <IconAction
            label="Eliminar sub-línea"
            size="sm"
            variant="subtle"
            className="text-peligro-600 hover:bg-peligro-50 dark:hover:bg-peligro-900/40"
            loading={eliminar.isPending}
            onClick={() => eliminar.mutate(linea.id)}
          >
            <IconTrash size={15} stroke={1.7} />
          </IconAction>
        )}
      </Td>
    </Tr>
  );
}
